import { createTheme, ThemeOptions } from '@mui/material/styles';

// Eve Echoes inspired color palette
const eveEchoesColors = {
  // Primary blues/cyans
  primaryDark: '#0a1929',
  primaryMain: '#1976d2',
  primaryLight: '#42a5f5',

  // Accent colors
  accentCyan: '#00e5ff',
  accentBlue: '#2196f3',
  accentPurple: '#7c4dff',

  // Background colors
  backgroundDefault: '#0a0e27',
  backgroundPaper: '#0f1419',
  backgroundElevated: '#1a1f2e',

  // Text colors
  textPrimary: '#e3f2fd',
  textSecondary: '#90caf9',
  textDisabled: '#546e7a',

  // Semantic colors
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: eveEchoesColors.primaryMain,
      light: eveEchoesColors.primaryLight,
      dark: eveEchoesColors.primaryDark,
    },
    secondary: {
      main: eveEchoesColors.accentCyan,
      light: '#4fffff',
      dark: '#00b2cc',
    },
    background: {
      default: eveEchoesColors.backgroundDefault,
      paper: eveEchoesColors.backgroundPaper,
    },
    text: {
      primary: eveEchoesColors.textPrimary,
      secondary: eveEchoesColors.textSecondary,
      disabled: eveEchoesColors.textDisabled,
    },
    success: {
      main: eveEchoesColors.success,
    },
    warning: {
      main: eveEchoesColors.warning,
    },
    error: {
      main: eveEchoesColors.error,
    },
    info: {
      main: eveEchoesColors.info,
    },
    divider: 'rgba(144, 202, 249, 0.12)',
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${eveEchoesColors.accentCyan} ${eveEchoesColors.backgroundDefault}`,
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: eveEchoesColors.backgroundDefault,
          },
          '&::-webkit-scrollbar-thumb': {
            background: eveEchoesColors.accentCyan,
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: eveEchoesColors.primaryLight,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          backgroundColor: eveEchoesColors.backgroundPaper,
        },
        elevation2: {
          backgroundColor: eveEchoesColors.backgroundElevated,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(0, 229, 255, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${eveEchoesColors.accentCyan}30`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: eveEchoesColors.backgroundElevated,
          borderBottom: `1px solid ${eveEchoesColors.accentCyan}30`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: eveEchoesColors.backgroundElevated,
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
export { eveEchoesColors };
